import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Stethoscope, Users, ShieldX, ArrowRight } from 'lucide-react';
import VeterinarianInfoForm from '../features/registration/components/VeterinarianInfoForm';
import EmailVerification from '../features/registration/components/EmailVerification';
import CreatePassword from '../features/registration/components/CreatePassword';

const VET_TOKEN = process.env.REACT_APP_VET_INVITE_TOKEN || 'VET_SECRET_2026';

// ─── Invalid Link Screen ────────────────────────────────────────────────────
const InvalidInviteScreen = () => {
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen w-full bg-gray-50 items-center justify-center px-6">
      <div className="bg-white rounded-2xl shadow-xl py-10 px-8 max-w-md w-full text-center">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50 mx-auto mb-5">
          <ShieldX size={32} className="text-red-500" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Invite Link</h2>
        <p className="text-gray-500 text-sm leading-relaxed mb-8">
          This link is invalid or has expired. Please contact your administrator for a valid invitation link to register as a Veterinarian.
        </p>
        <button
          onClick={() => navigate('/login')}
          className="w-full py-3 px-6 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-all shadow-sm"
        >
          Go to Sign In
        </button>
        <p className="text-xs text-gray-400 mt-4">
          Already registered?{' '}
          <a href="/login" className="text-indigo-600 font-semibold hover:underline">
            Sign in here
          </a>
        </p>
      </div>
    </div>
  );
};

// ─── Vet-Only Role Selection Card ───────────────────────────────────────────
const VetRoleSelectionCard = ({ onContinue }) => {
  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Join Vet Collab</h2>
      <p className="text-gray-500 mb-8 text-sm">Select your professional role to get started</p>

      <div className="space-y-4">
        <div className="w-full text-left p-5 rounded-xl border border-indigo-500 bg-white shadow-[0_0_0_1px_rgba(99,102,241,1)] relative overflow-hidden">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 p-3 rounded-full bg-purple-50">
              <Stethoscope size={22} className="text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-gray-900 text-[15px]">Veterinarian</span>
                <div className="w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-0.5 leading-relaxed">Licensed veterinary professional</p>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={onContinue}
        className="mt-8 w-full flex items-center justify-center gap-2 py-3 px-6 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all shadow-sm"
      >
        Continue
        <ArrowRight size={18} />
      </button>
    </div>
  );
};

const STEPS = ['Role', 'Info', 'Verify', 'Password'];

const StepIndicator = ({ current, total }) => (
  <div className="flex items-center gap-1.5 mb-8 md:hidden">
    {Array.from({ length: total }, (_, i) => (
      <React.Fragment key={i}>
        <div
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i < current ? 'bg-indigo-600' : i === current ? 'bg-indigo-400' : 'bg-gray-200'
          } ${i === current ? 'flex-[2]' : 'flex-1'}`}
        />
      </React.Fragment>
    ))}
  </div>
);

// ─── Vet-Only Registration Wizard ───────────────────────────────────────────
const VetOnlyRegisterWizard = () => {
  const [step, setStep] = useState(0);   // 0 = Role Selection, 1 = Info Form, 2 = Email Verify, 3 = Create Password
  const [userData, setUserData] = useState(null);
  const [registrationState, setRegistrationState] = useState(null);

  const handleRoleContinue = () => {
    setStep(1);
  };

  const handleUserInfoSubmit = (data) => {
    setUserData({ ...data, role: 'veterinarian' });
    setRegistrationState(null);
    setStep(2);
  };

  const handleVerified = () => {
    setStep(3);
  };

  // Left panel step indicators matching standard wizard structure
  const vetSteps = [
    { num: 1, label: 'Choose your role' },
    { num: 2, label: 'Your information' },
    { num: 3, label: 'Create password' },
  ];

  return (
    <div className="flex min-h-screen w-full bg-gray-50">
      {/* Left Side — Vet Branding */}
      <div className="hidden md:flex flex-col justify-between md:w-[35%] lg:w-[45%] bg-[#616ff6] p-10 lg:p-16 text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-8">
            <Users size={36} />
            <div>
              <h1 className="text-3xl font-bold tracking-tight leading-none mb-1">Vet Collab</h1>
              <p className="text-xs text-indigo-100 font-medium">powered by furcare</p>
            </div>
          </div>

          <div className="max-w-sm mt-16">
            <p className="text-base leading-relaxed text-indigo-50">
              Join thousands of veterinary professionals and students sharing knowledge, cases, and advancing animal care together.
            </p>
          </div>
        </div>

        {/* Vertical Step Indicator */}
        <div className="relative z-10 mt-auto mb-8">
          <div className="space-y-5">
            {vetSteps.map(({ num, label }) => {
              let isActive = false;
              if (num === 1 && step === 0) isActive = true;
              if (num === 2 && (step === 1 || step === 2)) isActive = true;
              if (num === 3 && step === 3) isActive = true;

              return (
                <div key={num} className="flex items-center gap-4">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                      isActive
                        ? 'bg-white text-[#616ff6]'
                        : 'bg-white/20 text-white'
                    }`}
                  >
                    {num}
                  </div>
                  <span
                    className={`text-sm transition-colors ${
                      isActive ? 'text-white font-medium' : 'text-indigo-100/70 font-light'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Side — Wizard Content */}
      <div className="flex-1 flex items-center justify-center py-2 px-6 lg:py-4">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="md:hidden flex items-center space-x-2 text-indigo-600 mb-8">
            <Users size={26} />
            <span className="font-bold text-2xl tracking-tight">Vet Collab</span>
          </div>

          <div className="bg-white rounded-2xl shadow-xl py-4 px-6 sm:py-5">
            <StepIndicator current={step} total={STEPS.length} />

            {/* Step 0: Veterinarian Role Card */}
            {step === 0 && (
              <VetRoleSelectionCard onContinue={handleRoleContinue} />
            )}

            {/* Step 1: Veterinarian Info Form */}
            {step === 1 && (
              <VeterinarianInfoForm
                initialData={userData}
                onNext={handleUserInfoSubmit}
                onBack={() => setStep(0)}
              />
            )}

            {/* Step 2: Email Verification */}
            {step === 2 && (
              <EmailVerification
                studentData={userData}
                registrationState={registrationState}
                setRegistrationState={setRegistrationState}
                onNext={handleVerified}
                onBack={() => setStep(1)}
              />
            )}

            {/* Step 3: Create Password */}
            {step === 3 && registrationState && (
              <CreatePassword
                registrationId={registrationState.registrationId}
                studentData={userData}
                onBack={() => setStep(2)}
              />
            )}

            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-center text-sm text-gray-500">
                Already have an account?{' '}
                <a href="/login" className="text-[#616ff6] font-semibold hover:text-indigo-800 transition-colors">
                  Sign in
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page — Validates token from URL ───────────────────────────────────
const VetInviteRegisterPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isValid = token === VET_TOKEN;

  if (!isValid) {
    return <InvalidInviteScreen />;
  }

  return <VetOnlyRegisterWizard />;
};

export default VetInviteRegisterPage;
